import { useState } from 'react';

import { getConfigValue } from 'utils/environment';

import { IBraintree } from './types';

type PayPalCheckoutInstance = {
  loadPayPalSDK: (options: { vault: boolean }) => Promise<void>;
  createPayment: (options: {
    flow: string;
    amount: number;
    currency: string;
    intent: string;
    requestBillingAgreement: boolean;
    billingAgreementDescription: string;
    billingAgreementDetails: { description: string };
    enableShippingAddress: boolean;
    shippingAddressEditable: boolean;
  }) => Promise<any>;
  tokenizePayment: (
    data: unknown,
    callback: (err: Error, payload: TokenizationResult) => void
  ) => void;
};

type VenmoInstance = {
  isBrowserSupported: () => boolean;
  tokenize: () => Promise<TokenizationResult>;
};

type DataCollectorInstance = {
  deviceData: string;
};

type TokenizationResult = {
  nonce: string;
  details: object;
};

type PaypalButtonsOptions = {
  style: {
    color: string;
    shape: string;
    height: number;
    disableMaxWidth: boolean;
  };
  fundingSource: string;
  createBillingAgreement: () => Promise<void>;
  onApprove: (data: unknown) => Promise<void>;
  onCancel: () => void;
  onError: (err: Error) => void;
};

declare global {
  interface Window {
    braintree?: {
      client: {
        create: (options: { authorization: string }) => Promise<unknown>;
      };
      paypalCheckout: {
        create: (options: { client: unknown }) => Promise<PayPalCheckoutInstance>;
      };
      dataCollector: {
        create: (options: { client: unknown; paypal: boolean }) => Promise<DataCollectorInstance>;
      };
      venmo: {
        create: (options: {
          client: unknown;
          allowDesktop: boolean;
          mobileWebFallBack: boolean;
          allowDesktopWebLogin: boolean;
          paymentMethodUsage: string;
        }) => Promise<VenmoInstance>;
      };
    };
    paypal?: {
      Buttons: (options: PaypalButtonsOptions) => { render: (selector: string) => void };
      FUNDING: { PAYPAL: string };
    };
  }
}

const SCRIPTS = [
  { id: 'braintree-client', url: 'https://js.braintreegateway.com/web/3.97.1/js/client.min.js' },
  {
    id: 'braintree-checkout',
    url: 'https://js.braintreegateway.com/web/3.97.1/js/paypal-checkout.min.js',
  },
  {
    id: 'braintree-collector',
    url: 'https://js.braintreegateway.com/web/3.97.1/js/data-collector.min.js',
  },
  {
    id: 'braintree-venmo',
    url: 'https://js.braintreegateway.com/web/3.97.1/js/venmo.min.js',
  },
];

function loadScript(id: string, url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const exists = document.getElementById(id);
    if (exists) {
      resolve();
      return;
    }
    const el = document.createElement('script');
    el.src = url;
    el.id = id;
    el.onload = () => resolve();
    el.onerror = () => reject();
    document.body.appendChild(el);
  });
}

async function embedScripts(): Promise<void> {
  for (const script of SCRIPTS) {
    await loadScript(script.id, script.url);
  }
}

export const useBraintree = ({ onResult, onError }: IBraintree) => {
  const [isLoading, setIsLoading] = useState(false);

  const { braintreeAuthorizationKey, braintreeBillingAgreementDescription } = getConfigValue({
    key: 'firstData',
    defaultValue: {},
  });

  const loadPayPalButton = async ({
    amount,
    selector,
  }: {
    amount: number;
    selector: string;
  }): Promise<void> => {
    setIsLoading(true);

    if (!window.braintree) {
      await embedScripts();
    }

    if (!window.braintree) {
      return onError(new Error('Braintree scripts did not load properly'));
    }

    const clientInstance = await window.braintree.client.create({
      authorization: braintreeAuthorizationKey,
    });

    const [paypalCheckoutInstance, dataCollectorInstance] = await Promise.all([
      window.braintree.paypalCheckout.create({
        client: clientInstance,
      }),
      window.braintree.dataCollector.create({
        client: clientInstance,
        paypal: true,
      }),
    ]);

    await paypalCheckoutInstance.loadPayPalSDK({
      vault: true,
    });

    if (!window.paypal) {
      return onError(new Error('PayPal SDK did not load properly'));
    }

    //  paypalCheckoutInstance.tokenizePayment()

    const buttons = await window.paypal.Buttons({
      style: {
        color: 'gold',
        shape: 'pill',
        height: 44,
        disableMaxWidth: true,
      },
      // @ts-ignore
      fundingSource: window.paypal.FUNDING.PAYPAL,
      createBillingAgreement() {
        return paypalCheckoutInstance.createPayment({
          flow: 'vault',
          amount,
          currency: 'USD',
          intent: 'capture',
          requestBillingAgreement: true,
          billingAgreementDescription: braintreeBillingAgreementDescription,
          billingAgreementDetails: {
            description: braintreeBillingAgreementDescription,
          },
          enableShippingAddress: false,
          shippingAddressEditable: false,
        });
      },
      async onApprove(data: unknown) {
        return paypalCheckoutInstance.tokenizePayment(data, function (
          err: Error,
          payload: TokenizationResult
        ) {
          if (err) {
            setIsLoading(false);
            onError(err);
            return;
          }
          try {
            const details = JSON.stringify(payload.details);
            const deviceData = JSON.parse(dataCollectorInstance.deviceData).correlation_id;
            const nonce = payload.nonce;
            onResult({ nonce, deviceData, details });
          } catch (err) {
            onError(new Error('Failed to parse device data'));
          }
          setIsLoading(false);
        });
      },
      onCancel() {
        setIsLoading(false);
        onError(new Error('PayPal payment cancelled'));
      },
      onError(err: Error) {
        setIsLoading(false);
        onError(err);
      },
    });

    if (document.querySelector(selector) !== null) {
      buttons.render(selector);
    }
    setIsLoading(false);
  };

  const loadVenmoUI = async (): Promise<void> => {
    setIsLoading(true);

    if (!window.braintree) {
      await embedScripts();
    }

    if (!window.braintree) {
      return onError(new Error('Braintree scripts did not load properly'));
    }

    if (!window.braintree.venmo) {
      return onError(new Error('Venmo SDK did not load properly'));
    }

    const clientInstance = await window.braintree.client.create({
      authorization: braintreeAuthorizationKey,
    });

    const [venmoInstance, dataCollectorInstance] = await Promise.all([
      window.braintree.venmo.create({
        client: clientInstance,
        allowDesktop: true,
        mobileWebFallBack: false,
        allowDesktopWebLogin: true,
        paymentMethodUsage: 'multi_use',
      }),
      window.braintree.dataCollector.create({
        client: clientInstance,
        paypal: true,
      }),
    ]);
    if (!venmoInstance.isBrowserSupported()) {
      onError(new Error('Browser does not support Venmo'));
      return;
    }

    try {
      const payload = await venmoInstance.tokenize();
      const deviceData = JSON.parse(dataCollectorInstance.deviceData).correlation_id;
      const details = JSON.stringify(payload.details);
      const nonce = payload.nonce;
      onResult({ details, deviceData, nonce });
    } catch (err) {
      onError(err as Error);
    }
    setIsLoading(false);
  };

  return {
    isLoading,
    loadPayPalButton,
    loadVenmoUI,
  };
};
